import uuidV4 from 'uuid/v4';
import {
  Config,
  CognitoIdentityCredentials,
  S3,
} from 'aws-sdk';
import {
  CognitoUserPool,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUser,
} from 'amazon-cognito-identity-js';
import fetchUserProfileQuery from 'Gql/users/queries/fetchUserProfile.graphql';
import fetchUserRolesQuery from 'Gql/users/queries/fetchUserRoles.graphql';
import fetchAppVersionQuery from 'Gql/app/queries/fetchAppVersion.graphql';
import fetchLoginUserQuery from 'Gql/users/queries/fetchLoginUser.graphql';
import setHomePageMutation from 'Gql/users/mutations/updateHomePage.graphql';
import setAssetUsersMutation from 'Gql/assets/mutations/updateAssetUsers.graphql';
import createFileMutation from 'Gql/files/mutations/createFile.graphql';
import UserProfile from 'Models/UserProfile';
import { Utils } from 'vue-graphql-models';
import { getEnv } from './utils';

/**
 * Authentication class
 *
 * @link https://github.com/aws/aws-amplify/tree/master/packages/amazon-cognito-identity-js
 */
export class AuthFactory {
  /**
   * Holds class instance
   *
   * @type {null|AuthFactory}
   */
  static instance = null;

  /**
   * Holds username
   *
   * @type {null|String}
   */
  username = null;

  /**
   * Holds Vue application instance
   *
   * @type {external:Vue}
   */
  vue = null;

  /**
   * Holds Vuex Store application instance
   *
   * @type {external:Vuex}
   */
  store = null;

  /**
   * Holds Apollo application instance
   *
   * @type {external:VueApollo}
   */

  apollo = null;

  /**
   * Holds AWS config
   *
   * @type Object
   */
  config = {};

  /**
   * Holds User Profile
   *
   * @type UserProfile
   */
  userProfile = {};

  /**
   * Holds User Details
   *
   * @type User
   */
  loginUser = {};


  /**
   * Holds App version
   *
   * @type {Float}
   */
  appVersion = 0.0;


  /**
   * Indicates whether appVersion loaded
   *
   * @type {Boolean}
   */
  appVersionLoaded = false;

  /**
   * Indicates whether userDetails loaded
   *
   * @type {Boolean}
   */
  loginUserLoaded = false;

  /**
   * Holds User Pool
   *
   * @type Object
   */
  userPool = {};

  /**
   * Defines form field to Cognito attribute mappings
   *
   * @type {{}}
   */
  fieldMaps = {
    fullName: 'name',
    phone: 'phone_number',
    address: 'address',
    email: 'email',
  };

  /**
   * Default S3 bucket for uploads
   *
   * @type {string}
   */
  defaultBucket = 'dhc-uploads-test';

  /**
   * Holds AuthenticationDetails data
   *
   * @type {CognitoIdentityServiceProvider.Types.InitiateAuthRequest}
   */
  authenticationDetails = null;

  /**
   * Holds actual user session
   *
   * @type {CognitoUserSession}
   */
  session = null;

  /**
   * Holds non-authorized user (for auth purposes)
   *
   * @type {CognitoUser}
   */
  guestUser = null;

  /**
   * Holds an authorized user
   *
   * @type {CognitoUser}
   */
  user = null;

  /**
   * Holds user roles
   *
   * @type {Array}
   */
  roles = [];

  /**
   * Indicates whether roles loaded
   *
   * @type {Boolean}
   */
  rolesLoaded = false;

  /**
   * Custom authorization flow type (stored as field for easier testing)
   *
   * @type {string}
   */
  authFlowType = 'CUSTOM_AUTH';

  /**
   * Defines a map of default route name for particular role(s)
   *
   * @type {Object}
   */
  defaultRoleRoutesMap = {
    assets: ['operator'],
    jobs: ['bidder'],
  };

  /**
   * Class constructor
   *
   * @param {String} Region - AWS region
   * @param {String} IdentityPoolId - AWS Cognito Identity Pool ID {@link https://docs.aws.amazon.com/cognito/latest/developerguide/identity-pools.html|See AWS documentation.}
   * @param {String} UserPoolId - AWS Cognito User Pool ID {@link https://docs.aws.amazon.com/cognito/latest/developerguide/cognito-user-identity-pools.html|See AWS documentation.}
   * @param {String} ClientId - AWS Cognito App ID {@link https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-client-apps.html|See AWS documentation.}
   * @returns {AuthFactory}
   */
  constructor({
    Region,
    IdentityPoolId,
    UserPoolId,
    ClientId,
  }) {
    if (AuthFactory.instance) {
      return AuthFactory.instance;
    }
    AuthFactory.instance = this;
    this.configure(Region, IdentityPoolId);
    this.initPool(UserPoolId, ClientId);
    this.user = this.userPool.getCurrentUser();
    this.refreshSession();
    return this;
  }

  /**
   * Configures Auth prerequisites
   *
   * @param Region
   * @param IdentityPoolId
   * @returns {AuthFactory}
   */
  configure(Region, IdentityPoolId) {
    Config.region = Region;
    Config.credentials = new CognitoIdentityCredentials({
      IdentityPoolId,
    });
    this.config = Config;
    return this;
  }

  /**
   * Returns a stored user
   *
   * @returns {Object}
   */
  getUser() {
    return this.user ? this.user : this.userPool.getCurrentUser();
  }

  /**
   * Returns class config
   *
   * @returns {Object}
   */
  getConfig() {
    return this.config;
  }

  /**
   * Returns User Pool instance
   *
   * @returns {Object}
   */
  getUserPool() {
    return this.userPool;
  }

  /**
   * Returns associated form field mapping
   *
   * @param field
   * @returns String
   */
  getFieldMap(field) {
    return this.fieldMaps[field];
  }

  /**
   * Creates and stores a new User Pool
   *
   * @param UserPoolId
   * @param ClientId
   * @returns {AuthFactory}
   */
  initPool(UserPoolId, ClientId) {
    this.userPool = new CognitoUserPool({
      UserPoolId,
      ClientId,
    });
    return this;
  }

  /**
   * Signs user up with AWS Cognito
   * @param email
   * @param fullName
   * @param phone
   * @param address
   * @returns {Promise<any>}
   */
  signUp({
    email,
    fullName,
    phone,
    address,
  }) {
    const phoneNumber = `+${phone}`.replace(/-/g, '');
    const password = `${uuidV4()}P`;
    this.username = phoneNumber;

    const attributeList = [
      new CognitoUserAttribute({
        Name: 'email',
        Value: email,
      }),
      new CognitoUserAttribute({
        Name: 'name',
        Value: fullName,
      }),
      new CognitoUserAttribute({
        Name: 'phone_number',
        Value: phoneNumber,
      }),
      new CognitoUserAttribute({
        Name: 'address',
        Value: address,
      }),
    ];

    return new Promise((resolve, reject) => {
      this.userPool.signUp(this.username, password, attributeList, null, (error, result) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(result.user);
      });
    });
  }

  /**
   * Re-sends confirmation code SMS
   *
   * @returns {Promise<any>}
   */
  resendConfirmationSMS() {
    return new Promise((resolve, reject) => {
      this.getUser().resendConfirmationCode((error, result) => {
        if (error) {
          return reject(error);
        }
        return resolve(result);
      });
    });
  }

  /**
   * Confirms a user with phone and code given
   *
   * @param phone
   * @param code
   * @returns {Promise<any>}
   */
  confirmUserWithCode(phone, code) {
    return new Promise((resolve, reject) => {
      const Username = `+${phone.replace(/-/g, '').replace(/\+/g, '')}`;
      const userData = {
        Username,
        Pool: this.getUserPool(),
      };
      const guestUser = new CognitoUser(userData);
      guestUser.confirmRegistration(String(code), true, (error) => {
        if (error) {
          return reject(error);
        }
        return resolve();
      });
    });
  }

  /**
   * Sends confirmation email for authenticated user
   *
   * @param code
   * @returns {Promise<any>}
   */
  // WIP
  // istanbul ignore next
  sendConfirmationEmail(code) {
    const that = this;
    return new Promise((resolve, reject) => {
      this.getUser().getAttributeVerificationCode('email', {
        onSuccess: result => resolve(result),
        onFailure: error => reject(error),
        inputVerificationCode() {
          that.getUser().verifyAttribute('email', code, this);
        },
      });
    });
  }

  /**
   * Attempts to refresh user session and re-initialize config
   *
   * @returns {Promise<any>}
   */
  refreshSession() {
    return new Promise((resolve) => {
      const user = this.getUser();
      // istanbul ignore if
      if (user === null) {
        this.setStoreAuthStatus(false);
        resolve(false);
        return;
      }
      user.getSession((error, session) => {
        if (error) {
          this.setStoreAuthStatus(false);
          resolve(false);
          return;
        }

        const { region } = Config;
        const { userPoolId } = this.userPool;

        Config.region = region;
        // Add the User's Id Token to the Cognito credentials login map.
        Config.credentials = new CognitoIdentityCredentials({
          IdentityPoolId: getEnv('AWS_POOL_ID'),
          // TODO Needs more attention to this section and ensure that `Logins`
          // TODO is always exists and match
          // TODO ... as sometimes it shows an error:
          // TODO ... {"__type":"NotAuthorizedException",
          // TODO ... "message":"Logins don't match. Please include at least one valid
          // TODO ... login for this identity or identity pool."}
          Logins: {
            [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: session.getIdToken().getJwtToken(),
          },
        }, this.config);

        this.session = session;
        this.setStoreAuthStatus(true);
        resolve(session.isValid());
      });
    });
  }

  /**
   * Retrieves user attribute(s)
   *
   * @param attribute
   * @returns {*}
   */
  getUserAttributes(attribute = null) {
    if (!this.session) {
      return null;
    }
    return attribute ? this.session.idToken.payload[attribute] : this.session.idToken.payload;
  }

  /**
   * Authenticates a user with custom password-less flow
   *
   * @param Username {string}
   * @returns {Promise<any>}
   */
  authenticateUser(Username) {
    const userData = {
      Username,
      Pool: this.getUserPool(),
    };
    const guestUser = new CognitoUser(userData);
    this.guestUser = guestUser;
    const authenticationData = {
      Username,
      Password: '',
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    guestUser.setAuthenticationFlowType(this.authFlowType);

    return new Promise((resolve, reject) => {
      guestUser.initiateAuth(authenticationDetails, {
        onSuccess: resolve,
        onFailure: reject,
        customChallenge: resolve,
      });
    });
  }

  /**
   * Tries to log in user with code provided sent by the challenge in `authenticateUser` call
   *
   * Possible reject errors:
   * 1. Session expired:
   *    code: "NotAuthorizedException"
   *    message: "Invalid session for the user."
   * 2. Non-String parameter (ex: Boolean)
   *    code: "SerializationException"
   *    message: "class java.lang.Boolean can not be converted to an String"
   * 3. Empty, null, undefined parameter
   *    code: "InvalidParameterException"
   *    message: "Missing required parameter ANSWER"
   * 4. Wrong code provided
   *    code: "NotAuthorizedException"
   *    message: "Incorrect username or password."
   *
   * @param code
   * @returns {Promise<any>}
   */
  loginWithCode(code) {
    return new Promise((resolve, reject) => {
      this.guestUser.sendCustomChallengeAnswer(code.replace(/-/, ''), {
        onSuccess: (session) => {
          // this.refreshSession
          this.session = session;
          this.getUser().signInUserSession = session;
          this.setStoreAuthStatus(true);
          resolve(session);
        },
        onFailure: reject,
      });
    });
  }

  /**
   * Logs out the user
   *
   * @returns {AuthFactory}
   */
  logout() {
    this.getUser().signOut();
    this.session = null;
    this.getUser().signInUserSession = null;
    this.setStoreAuthStatus(false);
    return this;
  }

  /**
   * Uploads a file into S3 bucket
   *
   * @param {string} file - File name
   * @param {string} content - File contents
   * @param {string} [prefix = ''] - File prefix
   * @param {string} [bucket=AuthFactory.defaultBucket] - Bucket name
   * @returns {Promise<string | never>}
   */
  uploadFile(file, content, prefix = '', bucket = this.defaultBucket) {
    const s3 = new S3(this.config);
    const nPrefix = prefix ? `${prefix.toLowerCase()}_` : '';
    const url = `${this.getUser().username.replace(/\+/, '')}/${nPrefix}${file}`;

    const params = {
      Bucket: bucket,
      Key: url,
      Body: content,
    };

    return s3.putObject(params).promise().then(() => url);
  }

  getFilePublicURL(file, bucket = this.defaultBucket) {
    const s3 = new S3(this.config);
    return s3.getPublicUrlHttp(bucket, file);
  }

  /**
   * Retrieves a file from S3
   *
   * @param {string} file - File name
   * @param {string} [bucket={this.defaultBucket}] - Bucket name
   * @return {Promise<PromiseResult<S3.GetObjectOutput, AWSError>>}
   */
  getUserFile(file, bucket = this.defaultBucket) {
    const s3 = new S3(this.config);

    const params = {
      Bucket: bucket,
      Key: file,
    };

    return s3.getObject(params).promise();
  }

  /**
   * Sets Vue Application
   *
   * @param {Vue} vue
   */
  async setApplication(vue) {
    this.vue = vue;
    this.store = vue.$store;
    this.apollo = vue.$apollo;

    this.setStoreOnlineStatus();
    const isLoggedIn = await this.isLoggedIn();
    this.setStoreAuthStatus(isLoggedIn);

    // istanbul ignore else
    if (isLoggedIn) {
      // await this.loadRoles();
    }
    this.store.dispatch('app/setLoading', { loading: false });
  }

  /**
   * Returns true if user session is active
   *
   * @returns {Promise<boolean>}
   */
  isLoggedIn() {
    return this.refreshSession();
  }

  /**
   * Stores authentication status in vuex store
   *
   * @param authenticated {boolean}
   */
  setStoreAuthStatus(authenticated) {
    if (!this.store) {
      return;
    }
    this.store.dispatch('app/setAuthenticated', { authenticated });
  }

  /* Stores online status in store
   *
   */
  setStoreOnlineStatus() {
    if (!this.store) {
      return;
    }
    this.store.dispatch('app/watchOnlineOffline');
  }

  /**
   * Loads user profile
   *
   * @returns {Promise<UserProfile>}
   */
  async loadUserProfile() {
    const { data: { fetchUserProfile } } = await this.apollo.query({
      query: fetchUserProfileQuery,
    });
    const sectionParams = fetchUserProfile && fetchUserProfile.length ? fetchUserProfile[0] : [];
    this.userProfile = new UserProfile(this.vue, sectionParams);
    this.setStoreProfileCompletenessStatus(this.userProfile.isComplete());
    return this.userProfile;
  }

  /**
   * Stores user profile completeness status
   *
   * @param profileComplete {boolean}
   */
  setStoreProfileCompletenessStatus(profileComplete) {
    // istanbul ignore else
    if (!this.store) {
      return;
    }
    this.store.dispatch('app/setProfileComplete', { profileComplete });
  }

  /**
   * Loads user roles from the backend
   *
   * @returns {Promise}
   */

  async loadLoginUser(force = false) {
    // istanbul ignore else

    try {
      if (this.loginUserLoaded && !force) {
        return this.loginUser;
      }
      const { data: { fetchLoginUser } } = await this.apollo.query({
        query: fetchLoginUserQuery,
      });
      this.loginUser = fetchLoginUser;
      this.loginUserLoaded = true;
      return this.loginUser;
    } catch (err) {
      if (window.location.href !== `${process.env.VUE_APP_SITE_URL}/login`) {
        // window.location.href = `${process.env.VUE_APP_SITE_URL}/login`;
      }
      return this.loginUser;
    }
  }

  async loadAppVersion() {
    // istanbul ignore else
    if (this.appVersionLoaded) {
      return this.appVersion;
    }
    const { data: { fetchAppVersion } } = await this.apollo.query({
      query: fetchAppVersionQuery,
    });
    this.appVersion = fetchAppVersion;
    this.appVersionLoaded = true;
    return this.appVersion;
  }

  /**
   * Set homePage for login user
   *
   * @returns {Boolean}
   */
  async setHomePage(route = 'assets') {
    const { data: { updateHomePage } } = await this.apollo.mutate({
      mutation: setHomePageMutation,
      fetchPolicy: 'no-cache',
      variables: {
        defaultRoute: route,
      },
    });

    if (updateHomePage) {
      this.loginUser = updateHomePage;
      this.loginUserLoaded = true;
      return true;
    }
    return false;
  }


  async updateAssetUsers(asset, userList, alertsSubscribersList) {
    await this.apollo.mutate({
      mutation: setAssetUsersMutation,
      fetchPolicy: 'no-cache',
      variables: {
        asset,
        userList,
        alertsSubscribersList,
      },
    });
  }

  async storeLabTestFile(data, attachTo) {
    await this.apollo.mutate({
      mutation: createFileMutation,
      fetchPolicy: 'no-cache',
      variables: {
        data,
        attachTo,
      },
    });
  }

  /**
   * Loads user roles from the backend
   *
   * @returns {Promise<array>}
   */
  async loadRoles() {
    // istanbul ignore else
    await this.loadLoginUser();
    if (this.rolesLoaded) {
      return this.roles;
    }
    const { data: { fetchUserRoles } } = await this.apollo.query({
      query: fetchUserRolesQuery,
    });
    this.roles = fetchUserRoles;
    this.rolesLoaded = true;
    return this.roles;
  }

  /**
   * Checks whether user has specified role(s)
   *
   * @param role {string|array}
   * @returns {Promise<Boolean>}
   */
  async hasRole(role) {
    const roles = Array.isArray(role) ? role : [role];
    await this.loadRoles();
    const intersected = Utils.intersect(this.roles.map(r => r.name), roles);
    return intersected.length === roles.length;
  }

  /**
   * Returns a default route for a particular role
   *
   * @returns {string}
   */
  async getRoleDefaultRoute() {
    return await this.hasRole('operator') ? 'assets' : 'jobs';
  }

  /**
   * Checks if the route is permitted for a particular role
   *
   * @param to {object}
   * @returns {Promise<Boolean>}
   */
  isRoutePermitted(to) {
    const metas = Utils.findRouteMetas(to);
    // istanbul ignore else
    if (!metas.roles || !metas.roles.length) {
      return Promise.resolve(true);
    }
    return this.hasRole(metas.roles);
  }
}

const Auth = new AuthFactory({
  Region: getEnv('AWS_REGION'),
  IdentityPoolId: getEnv('AWS_POOL_ID'),
  UserPoolId: getEnv('AWS_USER_POOL_ID'),
  ClientId: getEnv('AWS_CLIENT_ID'),
});

export default Auth;
